import { useCallback, useEffect, useState } from 'react'
import {
  CAPABILITIES,
  describeBundleEvent,
  describeOutsideEvidence,
  isDenial,
  tenantOf,
  toAuditCsv,
} from '@qim/shared'
import { ApiError, fetchAudit, fetchAuditDenials, fetchTenantAudit } from '../api.js'
import { formatInstant } from '../format.js'
import type { AuditEvent, AuditFilter, Capability } from '@qim/shared'
import type { JSX } from 'react'

/**
 * The audit trail.
 *
 * ## Read-only, and it could not be otherwise
 *
 * There is no write path to offer. `AuditRepository` exposes no update or delete
 * method, `AuditSink` declares only `record`, and the Lambda's IAM policy grants
 * no `dynamodb:DeleteItem` — so "strictly read-only in the UI" is a property of
 * the system rather than a restraint this screen exercises.
 *
 * ## Filtering narrows; it does not widen
 *
 * Every filter here is a preference sent to the API, which decides what the
 * viewer may see from their scope before applying any of it. A scoped viewer who
 * clears every filter still sees only their own customers. That separation is in
 * `shared/src/audit/viewing.ts` and tested there, precisely so nobody has to
 * trust this screen.
 *
 * ## Why refusals have their own request
 *
 * `deniedOnly` on the main read filters what was *read*; the denials endpoint
 * reads only what was *denied*, from a sparse index. On a busy day those are
 * different answers — a quiet trickle of refusals would fall off the end of a
 * newest-first window full of allows, which is the signal the trail exists for.
 * So the toggle changes which endpoint is called, not just which rows show.
 */

const outcomeOf = (event: AuditEvent): { readonly word: string; readonly mark: string } => {
  if (event.kind === 'access-change') return { word: 'Access changed', mark: '±' }
  // Its own word, not "Access changed": what an account accepts and who may
  // reach it are different facts, which is why they are different events.
  if (event.kind === 'account-setting-change') return { word: 'Account setting', mark: '±' }
  if (event.kind === 'lifetime-change') return { word: 'Lifetime', mark: '±' }
  if (event.kind === 'tenant-created') return { word: 'Customer created', mark: '+' }
  if (event.kind === 'budget-change') return { word: 'Budget', mark: '±' }
  // Its own word: nothing changed, but something left — a file of this
  // customer's operational detail, handed to the person named beside it.
  if (event.kind === 'diagnostics-bundle') return { word: 'Diagnostics collected', mark: '↓' }
  if (event.kind === 'destroyed-outside') {
    return { word: event.marked ? 'Marked destroyed' : 'Mark withdrawn', mark: '±' }
  }
  // IM-102 and IM-39. Each outcome its own word: "approved" and "auto-approved"
  // are different facts, and the second names a setting, not a person.
  if (event.kind === 'approval-decision') {
    // IM-176. Its own words: nobody approved it and nobody could have, which
    // "Approval no-approver" would say badly.
    if (event.outcome === 'no-approver') return { word: 'Went ahead: no approver', mark: '▶' }
    return {
      word: `Approval ${event.outcome}`,
      mark: event.outcome === 'rejected' || event.outcome === 'expired' ? '⊘' : '⏸',
    }
  }
  if (event.kind === 'protection-change') {
    return { word: event.protected ? 'Protected' : 'Protection removed', mark: '±' }
  }
  if (event.kind === 'approval-settings-change') return { word: 'Auto-approve', mark: '±' }
  return event.allowed
    ? { word: 'Allowed', mark: '✓' }
    : { word: `Denied — ${event.denialReason ?? 'unknown'}`, mark: '✕' }
}

/** A word and a shape first; colour is third, as everywhere in this console. */
const Outcome = ({ event }: { readonly event: AuditEvent }): JSX.Element => {
  const { word, mark } = outcomeOf(event)
  const kind =
    event.kind === 'access-change' ||
    event.kind === 'account-setting-change' ||
    event.kind === 'lifetime-change' ||
    event.kind === 'tenant-created' ||
    event.kind === 'destroyed-outside' ||
    event.kind === 'budget-change' ||
    event.kind === 'approval-decision' ||
    event.kind === 'protection-change' ||
    event.kind === 'approval-settings-change' ||
    event.kind === 'diagnostics-bundle'
      ? 'change'
      : isDenial(event)
        ? 'denied'
        : 'allowed'
  return (
    <span className={`outcome outcome-${kind}`}>
      <span aria-hidden="true">{mark}</span> {word}
    </span>
  )
}

/**
 * `null` on either side is a real state, and they mean different things.
 *
 * `before: null` is a first grant; `after: null` is a revocation — which the
 * event type could not express until IM-64, and which the compiler caught here
 * the moment it could. Rendering either as "none" would be accurate; saying
 * "revoked" is what an auditor is scanning for.
 */
const describeAccessChange = (event: Extract<AuditEvent, { kind: 'access-change' }>): string => {
  const from = event.before === null ? 'no access' : event.before.role
  const to = event.after === null ? 'revoked' : event.after.role
  return `${event.subjectEmail}: ${from} → ${to}`
}

/**
 * What an account setting change says, rather than what it was allowed to do.
 *
 * `null` before is "nobody had decided", which reads differently from somebody
 * having turned it off — the same distinction the access change draws on its
 * own null.
 */
const describeSettingChange = (
  event: Extract<AuditEvent, { kind: 'account-setting-change' }>,
): string => {
  const from = event.before === null ? 'unset' : event.before ? 'on' : 'off'
  return `${event.accountKey} ${event.setting}: ${from} → ${event.after ? 'on' : 'off'}`
}

const describe = (event: AuditEvent): string => {
  switch (event.kind) {
    case 'access-change':
      return describeAccessChange(event)
    case 'account-setting-change':
      return describeSettingChange(event)
    case 'lifetime-change':
      // `null` before is "nobody had said", which reads differently from
      // somebody having chosen indefinite.
      return `${event.deploymentId}: ${event.before ?? 'none set'} → ${event.after}`
    case 'budget-change': {
      const side = (b: typeof event.before): string =>
        b === null
          ? 'none'
          : `$${String(b.monthlyAmount)}, warn at ${String(b.warnAtPercent)}%, ${b.blocksNewInstances ? 'blocks new instances' : 'report only'}`
      return `platform budget: ${side(event.before)} → ${side(event.after)}`
    }
    case 'destroyed-outside':
      // The scan it rested on is the whole justification, so it is said.
      return `${event.deploymentId}: ${event.marked ? 'destroyed outside the control plane' : 'mark withdrawn'} (scan ${event.scannedAt}: ${describeOutsideEvidence(event.evidence)})`
    case 'diagnostics-bundle':
      // What could not be read is said, not only what was: "we tried and
      // could not" is part of the answer to "what did Qrvey take".
      return describeBundleEvent(event)
    case 'tenant-created':
      // The kind is the part worth reading: it cannot be changed afterwards.
      return `${event.name} as ${event.tenantKind === 'qrvey' ? 'Qrvey' : 'customer'}`
    case 'approval-decision':
      // Requester and decider on one row: the actor column is the decider.
      return `${event.command} ${event.runId}${event.deploymentId === undefined ? '' : ` on ${event.deploymentId}`}, asked by ${event.requestedBy}, needs ${event.requirement}${
        event.reason === undefined ? '' : ` — ${event.reason}`
      }${event.setting === undefined ? '' : ` (${event.setting})`}`
    case 'protection-change':
      return `${event.deploymentId} in ${event.accountKey}: ${event.protected ? 'protected' : 'protection removed'}`
    case 'approval-settings-change': {
      const side = (commands: readonly string[] | null): string =>
        commands === null ? 'not chosen' : commands.length === 0 ? 'nothing' : commands.join(', ')
      return `run without asking: ${side(event.before)} → ${side(event.after)}`
    }
    case 'authorization-decision':
      return `${event.method} ${event.path}`
  }
}

/**
 * The trail — across customers at `/audit`, or one customer's as that
 * customer's Audit tab.
 *
 * With `tenantId` it reads `GET /tenants/{tenantId}/audit`, which is authorized
 * against the customer in the path and leaves out untenanted events by design
 * ("who touched *this* customer"). The Customer field goes, because the path
 * already says which; and Refusals only goes, because that is a different
 * endpoint over a sparse index that has no per-tenant form — the audit screen
 * still offers it, filtered to this customer.
 */
export const Audit = ({
  baseUrl,
  tenantId,
}: {
  readonly baseUrl: string
  readonly tenantId?: string
}): JSX.Element => {
  const [filter, setFilter] = useState<AuditFilter>({})
  const [events, setEvents] = useState<readonly AuditEvent[]>([])
  const [days, setDays] = useState<readonly string[]>([])
  const [loading, setLoading] = useState(true)
  const [failure, setFailure] = useState<string | undefined>(undefined)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setFailure(undefined)
    try {
      if (tenantId !== undefined) {
        const page = await fetchTenantAudit(baseUrl, tenantId, filter)
        setEvents(page.events)
        setDays(page.days)
      } else if (filter.deniedOnly === true) {
        const page = await fetchAuditDenials(baseUrl, filter)
        setEvents(page.denials)
        setDays(page.days)
      } else {
        const page = await fetchAudit(baseUrl, filter)
        setEvents(page.events)
        setDays(page.days)
      }
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 400
          ? 'That date range is not valid — the start must not be later than the end.'
          : 'Could not read the audit trail just now.',
      )
    } finally {
      setLoading(false)
    }
  }, [baseUrl, filter, tenantId])

  useEffect(() => {
    void load()
  }, [load])

  const set = <K extends keyof AuditFilter>(key: K, value: AuditFilter[K]): void =>
    setFilter((current) => {
      const next = { ...current }
      // Deleted rather than set to undefined or '': the API distinguishes absent
      // from empty, and an empty filter would match nothing.
      if (value === undefined || value === '' || value === false) delete next[key]
      else next[key] = value
      return next
    })

  const download = (): void => {
    const blob = new Blob([toAuditCsv(events)], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // Names the range, so two exports in a ticket are not both "audit.csv".
    link.download = `qrvey-audit-${tenantId === undefined ? '' : `${tenantId}-`}${days[0] ?? 'today'}-to-${days[days.length - 1] ?? 'today'}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <section className="panel">
        <h2>Audit</h2>
        <p className="lede muted">
          {tenantId === undefined
            ? 'Every authorization decision, allows and denies.'
            : 'Everything recorded against this customer, allows and denies.'}{' '}
          Append-only — there is no way to change or remove a record, here or anywhere.
        </p>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            void load()
          }}
        >
          <div className="field-row">
            <div className="field">
              <label htmlFor="from">From</label>
              <input
                id="from"
                type="date"
                value={filter.from ?? ''}
                onChange={(event) => set('from', event.target.value)}
                aria-describedby="range-hint"
              />
            </div>
            <div className="field">
              <label htmlFor="to">To</label>
              <input
                id="to"
                type="date"
                value={filter.to ?? ''}
                onChange={(event) => set('to', event.target.value)}
                aria-describedby="range-hint"
              />
            </div>
          </div>
          <p id="range-hint" className="hint">
            Up to 31 days — each day is its own query.
          </p>
          <div className="field-row">
            {tenantId !== undefined ? null : (
              <div className="field">
                <label htmlFor="tenantFilter">Customer</label>
                <input
                  id="tenantFilter"
                  value={filter.tenantId ?? ''}
                  onChange={(event) => set('tenantId', event.target.value)}
                  placeholder="exact customer id"
                />
              </div>
            )}
            <div className="field">
              <label htmlFor="actorFilter">Actor</label>
              <input
                id="actorFilter"
                value={filter.actor ?? ''}
                onChange={(event) => set('actor', event.target.value)}
                placeholder="any part of an email"
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="capabilityFilter">Action</label>
            <select
              id="capabilityFilter"
              value={filter.capability ?? ''}
              onChange={(event) =>
                set(
                  'capability',
                  (event.target.value === '' ? undefined : event.target.value) as
                    Capability | undefined,
                )
              }
            >
              <option value="">Any</option>
              {CAPABILITIES.map((capability) => (
                <option key={capability} value={capability}>
                  {capability}
                </option>
              ))}
            </select>
          </div>
          {/* Its own line, and its own label element: this changes which
            endpoint is read, not which rows are shown. */}
          {tenantId !== undefined ? null : (
            <label className="check" htmlFor="deniedOnly">
              <input
                id="deniedOnly"
                type="checkbox"
                checked={filter.deniedOnly === true}
                onChange={(event) => set('deniedOnly', event.target.checked)}
              />
              Refusals only
            </label>
          )}
          <div className="actions">
            <button type="submit" disabled={loading}>
              {loading ? 'Reading…' : 'Apply'}
            </button>
            {/* An export is a read of what is already on the screen. */}
            <button
              type="button"
              className="ghost"
              onClick={download}
              disabled={events.length === 0}
            >
              Export CSV
            </button>
          </div>
        </form>

        {failure === undefined ? null : <p className="failure">{failure}</p>}
      </section>

      <section className="panel">
        {loading ? (
          <p role="status">Reading…</p>
        ) : events.length === 0 ? (
          // Not "no activity": a scoped viewer with a narrow filter and a
          // customer who did nothing look identical from here, and claiming the
          // second would be a guess.
          <p role="status">Nothing matched. Widen the range or clear a filter.</p>
        ) : (
          // The Request column carries the full path, which is the widest thing
          // in the console and was simply cut off before IM-73 — unreachable,
          // and the column an auditor came for.
          <div className="table-scroll">
            <table>
              <caption>
                {events.length} record{events.length === 1 ? '' : 's'} over{' '}
                {days.length === 1 ? days[0] : `${days[0]} to ${days[days.length - 1]}`}
              </caption>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Outcome</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Action</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Request</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  // The kind too: a decision and the event it permitted share a
                  // request id and an instant (IM-54).
                  <tr key={`${event.at}#${event.requestId}#${event.kind}`}>
                    {/* Seconds, because two decisions in one request-storm are
                    otherwise indistinguishable, and nowrap because a timestamp
                    broken across three lines cannot be read down a column. */}
                    <td>
                      <time dateTime={event.at}>{formatInstant(event.at, true)}</time>
                    </td>
                    <td>
                      <Outcome event={event} />
                      {event.kind === 'authorization-decision' && event.breakGlass === true ? (
                        // Break-glass use is loud rather than limited, and this is
                        // the row an auditor is looking for.
                        <strong> break-glass</strong>
                      ) : null}
                    </td>
                    <td>
                      {event.actorEmail}
                      {event.kind === 'authorization-decision' && event.actorRole !== undefined ? (
                        // The role held *at the time*, not today's — assignments
                        // change, and this record is what explains the decision.
                        <p className="muted">{event.actorRole}</p>
                      ) : null}
                    </td>
                    {/* The capability the act required. A mutation event does
                      not carry one — it is not a decision — so it names the
                      capability that must have permitted it. */}
                    <td>
                      {event.kind === 'access-change'
                        ? 'access:manage'
                        : event.kind === 'account-setting-change'
                          ? 'account:settings'
                          : event.kind === 'lifetime-change'
                            ? 'records:manage'
                            : event.kind === 'tenant-created'
                              ? 'tenant:manage'
                              : event.kind === 'destroyed-outside'
                                ? 'records:manage'
                                : event.kind === 'budget-change'
                                  ? 'budget:manage'
                                  : event.kind === 'approval-decision'
                                    ? 'deployment:approve'
                                    : event.kind === 'protection-change'
                                      ? event.protected
                                        ? 'records:manage'
                                        : 'deployment:approve'
                                      : event.kind === 'approval-settings-change'
                                        ? 'approvals:manage'
                                        : event.kind === 'diagnostics-bundle'
                                          ? 'diagnostics:bundle'
                                          : event.capability}
                    </td>
                    <td>{tenantOf(event) ?? <span className="muted">—</span>}</td>
                    <td>
                      <code>{describe(event)}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
